import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DownArrow, UpArrow } from "../../components/svg/Icons";
import RowsPerPageSelector from "../../components/common/RowsPerPageSelector";
import Pagination from "../../components/common/Pagination";
import LogButton from "../../components/common/LogButton";
import { LeadSummaryDetailsStyles } from "../../styles/pages/Reports/LeadSummaryDetailsStyles";
import { PhoneCall, Calendar, CheckSquare, ArrowLeft } from "lucide-react";
import Loader from "../../components/common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

interface APILead {
  id: string;
  name: string;
  email: string;
  contact: string;
  source: string;
  status: string;
  stage: string;
  company_name: string;
  opportunity_amount: string;
  // assigned_user_name: string;
  log: string;
  call_count: number;
  meeting_count: number;
  task_count: number;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  contact: string;
  source: string;
  status: string;
  stage: string;
  company_name: string;
  opportunity_amount: string;
  // assigned_user_name: string;
  log: string;
  call_count: number;
  meeting_count: number;
  task_count: number;
}

const LeadSummaryDetails = () => {
  const location = useLocation();
  const { state } = location;
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Lead | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType] = useState<"stage" | "status" | "source">(
    (state?.type as "stage" | "status" | "source") || "source"
  );
  const [filterValue] = useState<string>(state?.value || "Campaign");

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
          setError("Unauthorized: Please log in.");
          return;
        }
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/leads/by-filter`,
          {
            type: filterType,
            value: filterValue,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          const formattedLeads: Lead[] = Array.isArray(response.data.data)
            ? response.data.data.map((lead: APILead) => ({
                id: lead.id,
                name: lead.name,
                email: lead.email,
                contact: lead.contact,
                source: lead.source,
                status: lead.status,
                stage: lead.stage,
                company_name: lead.company_name,
                opportunity_amount: lead.opportunity_amount,
                // assigned_user_name: lead.assigned_user_name,
                log: lead.log,
                call_count: lead.call_count || 0,
                meeting_count: lead.meeting_count || 0,
                task_count: lead.task_count || 0,
              }))
            : [];
          setLeads(formattedLeads);
        } else {
          setLeads([]);
        }
      } catch (err) {
        setError("An error occurred while fetching leads.");
        setLeads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [filterType, filterValue]);

  const filteredLeads = leads.filter((lead) => {
    const search = searchTerm.toLowerCase();
    return [
      lead.name,
      lead.email,
      lead.contact,
      lead.source,
      lead.status,
      lead.stage,
      lead.company_name,
      lead.opportunity_amount,
      // lead.assigned_user_name,
      lead.log,
    ].some((field) => field && field.toString().toLowerCase().includes(search));
  });

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    if (!sortColumn) return 0;

    const valA = a[sortColumn];
    const valB = b[sortColumn];

    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return 0;
  });

  const displayedLeads = sortedLeads.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredLeads.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Lead) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredLeads
      : sortedLeads.length
      ? sortedLeads
      : leads;

    if (!dataToDownload || dataToDownload.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((lead) => ({
      Name: lead.name,
      Email: lead.email,
      MobileNumber: lead.contact,
      Source: lead.source,
      Status: lead.status,
      Stage: lead.stage,
      "Company Name": lead.company_name,
      "Opportunity Amount": lead.opportunity_amount
        ? `₹${Number(lead.opportunity_amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : "₹0.00",
      // "Assigned User": lead.assigned_user_name,
      Log: lead.log,
      "Call Count": lead.call_count,
      "Meeting Count": lead.meeting_count,
      "Task Count": lead.task_count,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title
    XLSX.utils.sheet_add_aoa(worksheet, [["Lead Summary Details"]], {
      origin: "A1",
    });

    // 2. Merge title across columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in second row
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Style headers
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) => (row as any)[key]?.toString().length || 0
          ),
          "Lead Summary Details".length
        ) + 2,
    }));

    // 8. Create workbook and export
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LeadSummaryDetails");

    const fileName = searchTerm
      ? `LeadSummaryDetails_${searchTerm}.xlsx`
      : "LeadSummaryDetails.xlsx";
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className={LeadSummaryDetailsStyles.container}>
      <div className={LeadSummaryDetailsStyles.header}>
        <h3 className={LeadSummaryDetailsStyles.title}>Lead Summary Details</h3>
        <Link
          to="/lead-summary"
          className={LeadSummaryDetailsStyles.backButton}
        >
          <ArrowLeft className="inline-block mr-1" size={16} />
          Back
        </Link>
      </div>

      <div className={LeadSummaryDetailsStyles.tableCard}>
        <div className={LeadSummaryDetailsStyles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />

          <div className={LeadSummaryDetailsStyles.searchDownloadContainer}>
            <div className={LeadSummaryDetailsStyles.searchInputContainer}>
              <Search className={LeadSummaryDetailsStyles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={LeadSummaryDetailsStyles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              className={LeadSummaryDetailsStyles.downloadButton}
              onClick={handleDownload}
            >
              Download
              <DownloadIcon className={LeadSummaryDetailsStyles.downloadIcon} />
            </button>
          </div>
        </div>
        {error && <p className={LeadSummaryDetailsStyles.error}>{error}</p>}

        <div className={LeadSummaryDetailsStyles.tableContainer}>
          <table className={LeadSummaryDetailsStyles.table}>
            <thead>
              <tr className={LeadSummaryDetailsStyles.theadRow}>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>Name</p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("email")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>Email</p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th className={LeadSummaryDetailsStyles.th}>
                  <div className="flex items-center justify-between cursor-pointer">
                  <p className={LeadSummaryDetailsStyles.thText}>
                    Mobile Number
                  </p>
                   <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                    </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("source")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>Source</p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>Status</p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("stage")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>Stage</p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("company_name")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>
                      Company Name
                    </p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("opportunity_amount")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>
                      Opportunity Amount
                    </p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th>
                {/* <th
                  className={LeadSummaryDetailsStyles.thSortable}
                  onClick={() => handleSort("assigned_user_name")}
                >
                  <div className="flex items-center justify-between cursor-pointer">
                    <p className={LeadSummaryDetailsStyles.thText}>
                      Assigned User
                    </p>
                    <div className={LeadSummaryDetailsStyles.sortButtons}>
                      <DownArrow />
                      <UpArrow />
                    </div>
                  </div>
                </th> */}
                <th className={LeadSummaryDetailsStyles.th}>
                  <p className={LeadSummaryDetailsStyles.thText}>Task</p>
                </th>
                <th className={LeadSummaryDetailsStyles.th}>
                  <p className={LeadSummaryDetailsStyles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody className={LeadSummaryDetailsStyles.tbody}>
              {loading ? (
                <tr className="border-none">
                  <td
                    colSpan={11}
                    className={LeadSummaryDetailsStyles.loadingTd}
                  >
                    <Loader />
                  </td>
                </tr>
              ) : displayedLeads.length > 0 ? (
                displayedLeads.map((lead) => (
                  <tr key={lead.id} className={LeadSummaryDetailsStyles.tr}>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.name || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.email || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.contact || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.source || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.status || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.stage || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.company_name || "--"}
                    </td>
                    <td className={LeadSummaryDetailsStyles.td}>
                      {lead.opportunity_amount
                        ? `₹${Number(lead.opportunity_amount).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}`
                        : "--"}
                    </td>

                    {/* <td className={LeadSummaryDetailsStyles.td}>
                      {lead.assigned_user_name || "--"}
                    </td> */}
                    <td className="w-[60px] md:w-[90px] px-2 py-2 md:px-3 md:py-3 border border-gray-100 dark:border-white/[0.05]">
                      <div className="flex items-center gap-2">
                        {/* Phone Call Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Call"
                            bgColor="bg-gradient-to-br from-teal-500 to-teal-700"
                          >
                            <Link
                              to={`/call-list/${lead.id}`}
                              className="contents"
                            >
                              <button className="w-6 h-6 bg-gradient-to-br from-teal-500 to-teal-700 hover:from-teal-600 hover:to-teal-800 flex items-center justify-center rounded-md shadow transition">
                                <PhoneCall className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-teal-400 to-teal-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.call_count || 0}
                          </span>
                        </div>

                        {/* Meeting Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Meeting"
                            bgColor="bg-gradient-to-br from-purple-500 to-purple-700"
                          >
                            <Link
                              to={`/lead-meeting/${lead.id}`}
                              className="contents"
                            >
                              <button className="w-6 h-6 bg-purple-600 hover:bg-purple-700 flex items-center justify-center rounded-md shadow transition">
                                <Calendar className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-purple-500 to-purple-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.meeting_count || 0}
                          </span>
                        </div>

                        {/* Task Button */}
                        <div className="relative group">
                          <Tooltip
                            content="Task"
                            bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
                          >
                            <Link
                              to={`/lead-task/${lead.id}`}
                              className="contents"
                            >
                              <button className="w-6 h-6 bg-orange-600 hover:bg-orange-700 flex items-center justify-center rounded-md shadow transition">
                                <CheckSquare className="w-4 h-4 text-white" />
                              </button>
                            </Link>
                          </Tooltip>
                          <span className="absolute -top-1 -right-1 bg-gradient-to-br from-orange-500 to-orange-700 text-white text-[7px] font-semibold w-3 h-3 rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 group-active:scale-125 transition-transform duration-200">
                            {lead.task_count || 0}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className={LeadSummaryDetailsStyles.logTd}>
                      <div className={LeadSummaryDetailsStyles.actionButtons}>
                        <LogButton log={lead.log || "--"} />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="border-none">
                  <td
                    colSpan={11}
                    className={LeadSummaryDetailsStyles.noRecordsTd}
                  >
                    No records found...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredLeads.length}
          handlePageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
export default LeadSummaryDetails;
